import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, BookOpen, TrendingUp, Shield, Clock, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroAccent } from '@/components/shared/PageHero';
import { generateMultipleSchemas } from '@/lib/schema';

const AICareerPlanningResources = () => {
  const navigate = useNavigate();

  const guideSteps = [
    {
      step: 1,
      title: 'Understand Your Risk Score',
      description: 'Get your AI risk analysis report to see your 0-100% risk score and understand what it means for your career.'
    },
    {
      step: 2,
      title: 'Review Timeline Estimates',
      description: 'Examine near-term (1-3 years), medium-term (3-7 years), and long-term (7-15 years) risk levels.'
    },
    {
      step: 3,
      title: 'Identify Key Risk Factors',
      description: 'Understand which specific factors contribute to automation risk in your occupation.'
    },
    {
      step: 4,
      title: 'Develop Adaptation Strategies',
      description: 'Use the personalized recommendations to plan skills development and career transitions.'
    },
    {
      step: 5,
      title: 'Take Action',
      description: 'Implement your plan: upskill, network, and position yourself for roles that complement AI.'
    }
  ];

  const automationPatterns = [
    {
      title: 'Tasks Automate Before Jobs Do',
      description:
        'Occupations are bundles of tasks, and automation removes tasks one at a time. Bank tellers did not vanish when ATMs arrived — the job shifted from cash handling toward sales and service. The practical question is never "will my job disappear?" but "which of my tasks go first, and what fills the freed time?"',
      takeaway: 'Audit your own week: the routine, rule-based hours are the exposed ones. Shift your value toward judgment, relationships, and exceptions.'
    },
    {
      title: 'The Remaining Work Often Pays More',
      description:
        'When the routine bottom of an occupation automates, wage data frequently shows average pay rising while employment falls — the surviving roles are the harder, higher-judgment ones. You can see this signature on our trends pages: wages up, headcount down.',
      takeaway: 'Rising wages in a shrinking occupation are not reassurance. Check the employment line before concluding a field is safe.'
    },
    {
      title: 'Augmentation Pays a Premium Before Substitution Bites',
      description:
        'In most fields, the near-term effect of AI tools is a productivity gap between workers who use them well and workers who do not — and the gap shows up in pay and promotion long before any job is eliminated. The window where tool fluency is rare is exactly when it is most valuable.',
      takeaway: 'Learning to direct AI tools in your domain is the highest-return move available in the near term, regardless of your occupation\u2019s long-run risk score.'
    }
  ];

  const tools = [
    {
      title: 'AI Risk Calculator',
      description: 'Get an instant preview of your occupation\'s AI risk score',
      icon: Zap,
      action: 'Try Calculator',
      href: '/ai-career-analysis'
    },
    {
      title: 'PDF Risk Reports',
      description: 'Get comprehensive AI risk analysis for any occupation - free, by email',
      icon: FileText,
      action: 'Get Free Report',
      href: '/ai-career-analysis'
    },
    {
      title: 'Career Planning Guide',
      description: 'Step-by-step guide to using AI risk analysis for career planning',
      icon: BookOpen,
      action: 'Read Guide',
      href: '#guide'
    },
    {
      title: 'AI Economy Jobs',
      description: 'Salaries and growth projections for the careers AI is creating - from data scientists to data center trades',
      icon: TrendingUp,
      action: 'Explore Careers',
      href: '/ai-economy-jobs'
    },
    {
      title: 'The AI Wage Premium',
      description: 'Why workers with AI skills earn ~62% more, and how to position yourself for the premium',
      icon: Shield,
      action: 'Read Analysis',
      href: '/insights/ai-wage-premium'
    }
  ];

  // Generate HowTo schema
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Use AI Risk Analysis for Career Planning',
    description: 'A comprehensive guide on using AI replacement risk analysis to plan your career and future-proof your profession.',
    step: guideSteps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.title,
      text: step.description
    }))
  };

  const schemas = generateMultipleSchemas([howToSchema]);

  return (
    <>
      <SeoHead
        title="AI Career Planning Resources | Future-Proof Your Career | Wage Atlas"
        description="Learn how to use AI risk analysis for career planning. Guides, data-driven analysis, and tools to help you future-proof your career in the age of AI."
        canonical="https://wageatlas.com/resources/ai-career-planning/"
        schemas={schemas}
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <PageHero
          badge={{ icon: BookOpen, label: 'Resource Center' }}
          title={
            <>
              AI Career Planning
              <HeroAccent> Resources</HeroAccent>
            </>
          }
          subtitle="Guides, data-driven analysis, and tools to help you future-proof your career in the age of AI"
        />

        {/* How-To Guide */}
        <section id="guide" className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">How to Use AI Risk Analysis for Career Planning</h2>
              <p className="text-xl text-muted-foreground">
                A step-by-step guide to leveraging AI risk analysis for your career development
              </p>
            </div>

            <div className="space-y-6">
              {guideSteps.map((step, index) => (
                <Card key={index} className="border-l-4 border-l-primary">
                  <CardContent>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xl font-bold text-primary">{step.step}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                        <p className="text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How automation actually plays out */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">How Automation Actually Reshapes Occupations</h2>
              <p className="text-xl text-muted-foreground">
                Three patterns that show up repeatedly in the labor data — and what each one means for your plan
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {automationPatterns.map((pattern, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg">{pattern.title}</CardTitle>
                    <CardDescription>{pattern.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-primary/5 rounded">
                      <div className="text-sm font-semibold mb-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        What to do with this
                      </div>
                      <div className="text-sm text-muted-foreground">{pattern.takeaway}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Tools & Resources</h2>
              <p className="text-xl text-muted-foreground">
                Interactive tools to help you plan your career
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {tools.map((tool, index) => {
                const Icon = tool.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle className="text-lg">{tool.title}</CardTitle>
                      </div>
                      <CardDescription>{tool.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => navigate(tool.href)}
                      >
                        {tool.action}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 text-center bg-primary/5">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold mb-4">Ready to Future-Proof Your Career?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get your comprehensive AI risk analysis report today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90"
                onClick={() => navigate('/ai-career-analysis')}
              >
                <FileText className="w-5 h-5 mr-2" />
                Get Your Free AI Risk Report
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AICareerPlanningResources;
